import React, { useState, useEffect, useCallback } from 'react';
import AddFacility from './AddFacility';
import { Icons } from '@ohif/ui-next';
import facilityService from '../../services/facilityService';
import userService from '../../services/userService';
import type { Facility } from '../../types/facility.types';
import type { User, UserRole, UserStatus } from '../../types/user.types';

interface FacilityDetailsProps {
  facilityId?: string;
  facilityName?: string;
  facility?: Facility | unknown; // Accept full Facility type from API
}

function facilityAdminDetail(facilityUser: Facility['users']): User | null {
  if (!facilityUser) {
    return null;
  }

  return {
    id: facilityUser.id,
    full_name: facilityUser.full_name,
    email: facilityUser.email,
    mobile_number: facilityUser.mobile_number || '',
    gender: (facilityUser.gender as User['gender']) || 'Male',
    date_of_birth: '',
    username: facilityUser.username || '',
    employee_id: facilityUser.employee_id || null,
    role: 'Technician' as UserRole,
    facility_id: '',
    status: 'Active' as UserStatus,
  };
}

const FacilityDetails: React.FC<FacilityDetailsProps> = ({
  facilityId,
  facilityName,
  facility: initialFacility,
}) => {
  const [isEditable, setIsEditable] = useState(false);
  const [facility, setFacility] = useState<Facility | null>((initialFacility as Facility) || null);
  const [isLoading, setIsLoading] = useState(false);
  const [adminUser, setAdminUser] = useState<User | null>(null);

  const resolveAdminFromFacility = useCallback(async (facilityData: Facility) => {
    if (facilityData.users) {
      const userFromFacility = facilityAdminDetail(facilityData.users);
      if (userFromFacility) {
        setAdminUser(userFromFacility);
      }
      return;
    }
    if (facilityData.user_id) {
      const userResponse = await userService.getUserById(facilityData.user_id);
      if (userResponse.success && userResponse.data) {
        setAdminUser(userResponse.data);
      }
    }
  }, []);

  const fetchAdminFromInitialFacility = useCallback(async (facilityData: Facility) => {
    if (facilityData.users) {
      const userFromFacility = facilityAdminDetail(facilityData.users);
      if (userFromFacility) {
        setAdminUser(userFromFacility);
      }
      return;
    }
    if ('user_id' in facilityData && facilityData.user_id && !facilityData.users) {
      setIsLoading(true);
      const userResponse = await userService.getUserById(facilityData.user_id);
      if (userResponse.success && userResponse.data) {
        setAdminUser(userResponse.data);
      }
      setIsLoading(false);
    }
  }, []);

  // Fetch facility data and admin user details
  useEffect(() => {
    const fetchFacilityAndAdmin = async () => {
      if (initialFacility || !facilityId) {
        const facilityData = initialFacility as Facility | null;
        if (facilityData) {
          await fetchAdminFromInitialFacility(facilityData);
        }
        return;
      }

      setIsLoading(true);
      const response = await facilityService.getFacilityById(facilityId);

      if (response.success && response.data) {
        setFacility(response.data);
        await resolveAdminFromFacility(response.data);
      } else {
        console.error('Failed to fetch facility:', response.error);
      }

      setIsLoading(false);
    };

    fetchFacilityAndAdmin();
  }, [facilityId, initialFacility, fetchAdminFromInitialFacility, resolveAdminFromFacility]);

  const editButton = !isEditable ? (
    <button
      onClick={() => setIsEditable(true)}
      style={{
        height: '36px',
        padding: '0 12px',
        backgroundColor: '#1e3a8a',
        border: '1px solid #3b82f6',
        borderRadius: '4px',
        fontSize: '14px',
        fontWeight: 500,
        color: '#60a5fa',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        boxSizing: 'border-box',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.backgroundColor = '#1e40af';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.backgroundColor = '#1e3a8a';
      }}
    >
      <Icons.EditButton
        style={{
          width: '16px',
          height: '16px',
          color: '#60a5fa',
        }}
      />
      <span>Edit Details</span>
    </button>
  ) : null;

  return (
    <div
      style={{
        backgroundColor: 'var(--bg-primary)',
        minHeight: '100%',
        position: 'relative',
      }}
    >
      {isLoading ? (
        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          Loading facility details...
        </div>
      ) : (
        <AddFacility
          readOnly={!isEditable}
          initialFacility={facility ? { ...facility, adminUser } : null}
          hideHeader={true}
          hideFooter={!isEditable}
          isEditable={isEditable}
          setIsEditable={setIsEditable}
          editButton={editButton}
        />
      )}
    </div>
  );
};

export default FacilityDetails;
